import { apiClient } from './apiClient';

export interface NotificationPreference {
  id: string;
  userId: string;
  tenantId: string;
  category: string;
  channel: 'email' | 'sms' | 'push' | 'in_app';
  enabled: boolean;
  frequency?: 'immediate' | 'daily' | 'weekly' | 'never';
  quietHours?: { start: string; end: string };
  createdAt: string;
  updatedAt: string;
}

export interface NotificationData {
  id: string;
  tenantId: string;
  userId: string;
  type: string;
  category: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  channels: string[];
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  readAt?: string;
  sentAt?: string;
  deliveredAt?: string;
  failureReason?: string;
  retryCount: number;
  maxRetries: number;
  expiresAt?: string;
  actionUrl?: string;
  actionText?: string;
  groupKey?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationTemplate {
  id: string;
  tenantId: string;
  name: string;
  type: string;
  category: string;
  subject: string;
  emailTemplate?: string;
  smsTemplate?: string;
  pushTemplate?: string;
  inAppTemplate?: string;
  variables: string[];
  isActive: boolean;
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

class ComprehensiveNotificationService {
  // Notifications
  async getNotifications(
    userId: string,
    filters?: {
      type?: string;
      category?: string;
      status?: string;
      unreadOnly?: boolean;
      limit?: number;
      offset?: number;
    },
  ): Promise<{ notifications: NotificationData[]; total: number; unreadCount: number }> {
    return apiClient.get('/notifications', {
      userId,
      ...filters,
    });
  }

  async getNotification(notificationId: string): Promise<NotificationData> {
    return apiClient.get<NotificationData>(`/notifications/${notificationId}`);
  }

  async sendNotification(
    notificationData: Omit<
      NotificationData,
      | 'id'
      | 'status'
      | 'sentAt'
      | 'deliveredAt'
      | 'readAt'
      | 'retryCount'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<NotificationData> {
    return apiClient.post<NotificationData>('/notifications', notificationData);
  }

  async markAsRead(notificationId: string): Promise<NotificationData> {
    return apiClient.put<NotificationData>(`/notifications/${notificationId}/read`);
  }

  async markAllAsRead(userId: string): Promise<number> {
    const result = await apiClient.put<{ count: number }>(
      `/notifications/users/${userId}/read-all`,
    );
    return result.count;
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await apiClient.delete(`/notifications/${notificationId}`);
  }

  async getUnreadCount(userId: string): Promise<number> {
    const result = await apiClient.get<{ count: number }>(
      `/notifications/users/${userId}/unread-count`,
    );
    return result.count;
  }

  // Notification Preferences
  async getNotificationPreferences(userId: string): Promise<NotificationPreference[]> {
    return apiClient.get<NotificationPreference[]>(`/notifications/users/${userId}/preferences`);
  }

  async updateNotificationPreferences(
    userId: string,
    preferences: Partial<NotificationPreference>[],
  ): Promise<NotificationPreference[]> {
    return apiClient.put<NotificationPreference[]>(`/notifications/users/${userId}/preferences`, {
      preferences,
    });
  }

  // Real-time subscription (WebSocket)
  subscribeToNotifications(
    userId: string,
    onNotification: (notification: NotificationData) => void,
  ): () => void {
    const ws = new WebSocket(`${this.wsUrl}/notifications/${userId}`);

    ws.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);
        onNotification(notification);
      } catch (error) {
        console.error('Failed to parse notification:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }

  private get wsUrl() {
    return import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:3001';
  }
}

export const comprehensiveNotificationService = new ComprehensiveNotificationService();
